import { z } from 'zod'
import {
  IFRAME_URL,
  MAX_MODAL_DELAY_SECONDS,
  MAX_MODAL_FREQUENCY_DAYS,
  MODAL_BACKDROP_CLASS,
  MODAL_CLAIMED_WINDOW_IDENTIFIER,
  MODAL_CLOSE_BUTTON_CLASS,
  MODAL_DIALOG_CLASS,
  MODAL_DISMISSED_STORAGE_KEY_PREFIX,
  MS_PER_DAY,
} from '@/constants'
import { WidgetAttributesSchema } from '@/schemas/WidgetAttributesSchema'
import {
  BW_MODAL_CLOSE_READY_EVENT,
  BW_MODAL_DISMISS_EVENT,
} from '../../../constants'
import { createIframe } from './createIframe'
import { injectIframe } from './injectIframe'

type WidgetAttributes = z.infer<typeof WidgetAttributesSchema>

const WIDGET_ORIGIN = new URL(IFRAME_URL).origin

// Modal-mode snippets never inject inline. The first modal-mode snippet on
// the page claims the (single) modal slot and starts the open timer; any
// further modal-mode snippets are skipped entirely — one modal per page.
export function scheduleWidgetModal(widgetAttributes: WidgetAttributes) {
  if (window[MODAL_CLAIMED_WINDOW_IDENTIFIER]) {
    return
  }
  window[MODAL_CLAIMED_WINDOW_IDENTIFIER] = true

  // The schema guarantees modalDelay is a string of digits, so the only
  // clamping left to do is the 120-second ceiling.
  const delaySeconds = Math.min(
    Number(widgetAttributes.modalDelay),
    MAX_MODAL_DELAY_SECONDS,
  )

  setTimeout(() => {
    // Suppression is checked when the timer fires, not at schedule time, so
    // the freshest dismissal timestamp wins.
    if (
      isModalSuppressed(
        widgetAttributes.widgetId,
        widgetAttributes.modalFrequency,
      )
    ) {
      return
    }
    openWidgetModal(widgetAttributes)
  }, delaySeconds * 1000)
}

function openWidgetModal(widgetAttributes: WidgetAttributes) {
  const backdrop = document.createElement('div')
  backdrop.className = MODAL_BACKDROP_CLASS

  const dialog = document.createElement('div')
  dialog.className = MODAL_DIALOG_CLASS
  dialog.setAttribute('role', 'dialog')
  dialog.setAttribute('aria-modal', 'true')

  // Fallback for when the widget never renders its own close button (failed
  // load, error page). At phone widths the dialog covers the backdrop, so
  // without it the visitor could be trapped.
  const fallbackCloseButton = document.createElement('button')
  fallbackCloseButton.className = MODAL_CLOSE_BUTTON_CLASS
  fallbackCloseButton.setAttribute('type', 'button')
  fallbackCloseButton.setAttribute('aria-label', 'Close')
  fallbackCloseButton.textContent = '×'

  dialog.appendChild(fallbackCloseButton)
  backdrop.appendChild(dialog)

  // Body scroll is locked while the modal is open; keep the previous inline
  // values (usually empty) so closing restores whatever the host page had.
  const previousDocumentOverflow = document.documentElement.style.overflow
  const previousBodyOverflow = document.body.style.overflow
  const previouslyFocusedElement = document.activeElement

  // Covers Escape while focus is on the host page. Inside the iframe the
  // widget handles Escape and its own close button, and posts
  // BW_MODAL_DISMISS_EVENT.
  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close()
    }
  }

  // The iframe is created lazily — at open time, not at schedule time — via
  // the same primitives as inline injection, so the loader and the
  // iframe-resizer binding work unchanged inside the dialog.
  const iframe = createIframe(widgetAttributes, IFRAME_URL)

  const handleWidgetFrameMessage = (event: MessageEvent) => {
    if (
      event.origin !== WIDGET_ORIGIN ||
      event.source !== iframe.contentWindow
    ) {
      return
    }
    if (event.data?.type === BW_MODAL_CLOSE_READY_EVENT) {
      fallbackCloseButton.remove()
    }
    if (event.data?.type === BW_MODAL_DISMISS_EVENT) {
      close()
    }
  }

  const close = () => {
    document.removeEventListener('keydown', onKeydown)
    window.removeEventListener('message', handleWidgetFrameMessage)
    backdrop.remove()
    document.documentElement.style.overflow = previousDocumentOverflow
    document.body.style.overflow = previousBodyOverflow
    recordModalDismissal(widgetAttributes.widgetId)
    if (previouslyFocusedElement instanceof HTMLElement) {
      previouslyFocusedElement.focus()
    }
  }

  fallbackCloseButton.addEventListener('click', close)
  backdrop.addEventListener('click', (event) => {
    // The dialog is transparent, so a click on its empty area reads as a
    // backdrop click; clicks inside the widget never reach the host page.
    if (event.target === backdrop || event.target === dialog) {
      close()
    }
  })
  document.addEventListener('keydown', onKeydown)
  window.addEventListener('message', handleWidgetFrameMessage)

  document.documentElement.style.overflow = 'hidden'
  document.body.style.overflow = 'hidden'
  document.body.appendChild(backdrop)

  injectIframe(iframe, dialog)

  iframe.focus()
}

function suppressionKey(widgetId: string) {
  return `${MODAL_DISMISSED_STORAGE_KEY_PREFIX}${widgetId}`
}

function isModalSuppressed(widgetId: string, frequencyDays: string) {
  try {
    const dismissedAt = window.localStorage.getItem(suppressionKey(widgetId))
    if (!dismissedAt) {
      return false
    }
    const elapsedMs = Date.now() - Number(dismissedAt)
    if (elapsedMs < 0) {
      return false
    }
    return (
      elapsedMs <
      Math.min(Number(frequencyDays), MAX_MODAL_FREQUENCY_DAYS) * MS_PER_DAY
    )
  } catch {
    // localStorage can throw (e.g. Safari private mode, blocked storage);
    // fail open so the modal still shows.
    return false
  }
}

function recordModalDismissal(widgetId: string) {
  try {
    window.localStorage.setItem(suppressionKey(widgetId), String(Date.now()))
  } catch {
    // Best effort: without storage the modal may reopen on the next visit.
  }
}
